"use client";

import ProductPage from "@/components/ProductPage";
import { KassensystemHardwareSection } from "@/components/KassensystemHardwareSection";

import ImageHero from "./cashtex_schuhe.jpg";
import ImageHighlight from "./highlight.jpg";
import ImageFeaturePOS from "./feature-pos.jpg";
import ImageFeatureCustomers from "./feature-customers.jpg";
import ImageFeatureStock from "./feature-stock.jpg";
import ImageFeatureStats from "./feature-stats.jpg";
import ImageFeatureOrder from "./feature-order.jpg";
import ImageFeatureExport from "./feature-export.jpg";

import Section from "@/components/Section";
import ImageText from "@/components/ImageText";
import { Check } from "lucide-react";
import SpecialFeaturesSection from "@/components/SpecialFeaturesSection";
import { PricingCard } from "@/components/PricingSection";
import Image from "next/image";

export default function Page() {
  return (
    <ProductPage
      productName="Cashtex Profi – Kassensystem für den Schuheinzelhandel"
      hero={{
        title: "Cashtex Profi – Kassensystem für den Schuheinzelhandel",
        subtitle:
          "Speziell für den Schuhhandel entwickelt: Schnelltasten für Zubehör, flexible Größenverwaltung, Größenzuschläge und starke Verkaufsstatistiken – alles in einem System.",
        buttonLeft: {
          title: "Unverbindliche Anfrage",
          href: "/support",
        },
        buttonRight: { title: "Preise", href: "/preise" },
        image: ImageHero,
        imageAlt: "Cashtex Profi Kassensystem für den Schuhhandel",
      }}
      highlight={{
        image: ImageHighlight,
        bullets: [
          <>
            <strong>Größenverwaltung & Zuschläge</strong> – ideal für Modelle,
            Varianten und Größenpreise.
          </>,
          <>
            <strong>Schnelltasten für Zubehör</strong> (Pflege, Einlagen,
            Schnürsenkel) direkt im Verkauf.
          </>,
          <>
            <strong>Renner/Penner & Größen-Reports</strong> für klare
            Entscheidungen im Sortiment.
          </>,
          <>
            <strong>
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary underline hover:opacity-80"
              >
                GoBD
              </a>
              /
              <a
                href="https://www.hsp-steuer.de/2017/01/18/gobs-gobd-gdpdu-einfach-erklaert/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary underline hover:opacity-80"
              >
                GDPdU
              </a>
              -konform
            </strong>{" "}
            inkl. Kassenbuch und{" "}
            <a
              href="https://www.softguide.de/funktion/datev-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              DATEV
            </a>
            /
            <a
              href="https://www.softguide.de/funktion/fibu-export"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              FIBU-Export
            </a>
            /
            <a
              href="https://www.seeburger.com/de/ressourcen/wissenswertes/was-ist-edi"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              EDI-Schnittstelle
            </a>
            /
            <a
              href="https://www.bsi.bund.de/DE/Themen/Unternehmen-und-Organisationen/Standards-und-Zertifizierung/Schutz-vor-Manipulation-an-digitalen-Grundaufzeichnungen/Fragen-und-Antworten/fragen-und-antworten_node.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-primary underline hover:opacity-80"
            >
              TSE
            </a>
            -konform/Schnittstelle zu Ihrem Steuerberater.
          </>,
          <>
            <strong>Finanzamt- und Gesetzkonform</strong>
          </>,
        ],
      }}
      beforeVideo={
        <Section variant="transparent" className="py-4 md:py-6 mt-12">
          <ImageText
            imagePosition="right"
            imageSlot={
              <div className="relative overflow-hidden mt-12 rounded-2xl border border-black/10 bg-black shadow-sm h-auto md:h-100">
                <video
                  className="w-full h-auto md:h-full md:object-cover"
                  controls
                >
                  <source src="/VideoFürWebseite.mp4" type="video/mp4" />
                </video>
              </div>
            }
          >
            <div className="flex flex-col gap-2 mb-2">
              <Image
                src="/CTXr.svg"
                alt="Cashtex"
                width={120}
                height={36}
                className="object-contain"
              />
              <h2 className="text-3xl md:text-4xl font-semibold text-[#050a18]">
                Kassensystem für den Schuheinzelhandel
              </h2>
            </div>

            <p className="text-slate-700 leading-relaxed">
              Cashtex unterstützt Schuhhändler bei der Verwaltung von Größen,
              Varianten und Zubehör – schnell, übersichtlich und effizient im
              täglichen Verkauf.
            </p>

            <p className="mt-2 text-slate-700 leading-relaxed">
              Mit integrierten Verkaufsstatistiken und flexiblen
              Preisgestaltungen behalten Sie jederzeit den Überblick über Ihr
              Sortiment.
            </p>

            <ul className="mt-2 space-y-2">
              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Größen & Varianten:</strong> Schuhe einfach verwalten
                  und flexibel bepreisen.
                </span>
              </li>

              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Zubehörverkauf:</strong> Pflegeprodukte und Zubehör
                  direkt über Schnelltasten verkaufen.
                </span>
              </li>

              <li className="flex items-start gap-3 text-slate-700">
                <Check className="mt-1 text-brand-primary" size={18} />
                <span>
                  <strong>Analysen:</strong> Verkaufszahlen und Trends jederzeit
                  im Blick behalten.
                </span>
              </li>
            </ul>
          </ImageText>

          <SpecialFeaturesSection
            industries={[
              {
                title: "Einzelhandel Schuhe",
                standard: [
                  "Kassieren",
                  "Artikelverwaltung",
                  "Zahlungsarten",
                  "Tagesabschluss",
                  "Belegausgabe",
                  "Rabatte & Aktionspreise",
                  "Auswertungen",
                  "Mehrwertsteuer-Verwaltung",
                ],
                special: [
                  "Schneller und intuitiver Kassiervorgang im Alltag",
                  "Größenverwaltung pro Artikel und Marke",
                  "Übersichtliche Lagerübersicht nach Größe und Modell",
                  "Flexible Zahlungsarten und Belegausgabe",
                  "Klare Auswertungen für bessere Entscheidungen",
                  "Kundenverwaltung mit Kaufhistorie",
                  "Automatische Lagerbestandsüberwachung mit Mindestmengen",
                  "Saisonverwaltung für Kollektionen",
                ],
              },
            ]}
          />
        </Section>
      }
      faqs={[
        {
          q: "Unterstützt Cashtex individuelle Schnelltasten für Schuhzubehör?",
          a: "Ja, Sie können Zubehör mit individuell konfigurierbaren Schnelltasten schnell und einfach zum Verkauf hinzufügen – für einen reibungslosen Ablauf an der Kasse.",
        },
        {
          q: "Wie funktioniert die Artikelverwaltung bei verschiedenen Größen?",
          a: "Cashtex unterstützt Größenvarianten und Größenzuschläge – ideal für den Schuhhandel, um Bestände präzise nach Modell und Größe zu verwalten.",
        },
        {
          q: "Welche Statistiken sind möglich?",
          a: "Umsatzgrafiken, Bestandsberichte und Verkaufsstatistiken – auch im Vergleich zum Vorjahr – sind im System enthalten. So behalten Sie Ihre Geschäftsentwicklung jederzeit im Blick.",
        },
        {
          q: "Gibt es spezielle Berichte für Renner/Penner?",
          a: "Ja, Verkaufsberichte nach Mitarbeitenden, Lieferanten und Schuhgröße sowie Renner-/Penner-Listen sind verfügbar – für eine fundierte Einkaufs- und Sortimentsplanung.",
        },
        {
          q: "Wie starte ich mit der Demoversion?",
          a: "Einfach unverbindlich anfragen – wir melden uns zeitnah mit einem Terminvorschlag und richten die Demo individuell für Sie ein.",
        },
        {
          q: "Unterstützt Cashtex die Saisonverwaltung für Schuhkollektionen?",
          a: "Ja, Artikel lassen sich Saisons zuordnen – so behalten Sie den Überblick über aktuelle und auslaufende Kollektionen und können gezielt Abverkäufe planen.",
        },
        {
          q: "Kann ich Lieferanten und Bestellungen direkt in Cashtex verwalten?",
          a: "Ja, Cashtex bietet eine Lieferantenverwaltung mit Bestellhistorie – für schnelle Nachbestellungen direkt aus dem System heraus.",
        },
        {
          q: "Wie läuft die Einrichtung und Schulung ab?",
          a: "Nach einer persönlichen Beratung richtet unser Team das System für Sie ein. Schulungen und Support sind im Einführungsprozess inbegriffen.",
        },
      ]}
      features={[
        {
          src: ImageFeaturePOS,
          alt: "Cashtex Schuhe – schneller Verkauf an der Kasse",
          title: "Schneller Verkauf & Kassenprozess",
          desc: "Belege, Retouren, Umtausch und Rabattaktionen – direkt an der Kasse, ideal für Schuhgeschäfte.",
        },
        {
          src: ImageFeatureCustomers,
          alt: "Cashtex Schuhe – Kundenverwaltung",
          title: "Kundenverwaltung",
          desc: "Stammkunden mit Bonusprogrammen, Gutscheinen, Geburtstagsaktionen und Kundenkarte professionell betreuen.",
        },
        {
          src: ImageFeatureStock,
          alt: "Cashtex Schuhe – Artikel- und Lagerverwaltung",
          title: "Artikel- & Lagerverwaltung",
          desc: "Schuhgrößen, Farben und Modelle übersichtlich verwalten – mit Barcode-/Etikettendruck und Scanner-Inventur.",
        },
        {
          src: ImageFeatureStats,
          alt: "Cashtex Schuhe – Verkaufsstatistiken",
          title: "Verkaufsstatistiken",
          desc: "Analysen zu Bestsellern, Ladenhütern, Mitarbeitenden und Lieferanten – für fundierte Entscheidungen.",
        },
        {
          src: ImageFeatureOrder,
          alt: "Cashtex Schuhe – Bestellwesen und Wareneingang",
          title: "Bestellwesen & Wareneingang",
          desc: "Bestellungen nach Größen und Modellen, Kontrolle offener Lieferungen und Wareneingang per Scanner.",
        },
        {
          src: ImageFeatureExport,
          alt: "Cashtex Schuhe – Kassenbuch und Export",
          title: "Kassenbuch & Export",
          desc: "Revisionssicheres Kassenbuch, Einnahmen/Ausgaben sowie Exporte für DATEV, Lexware und FIBU.",
        },
      ]}
    >
      {/* PRICING CARDS */}
      <Section className="pb-24 bg-white border-y border-gray-50">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-7xl mx-auto px-4">
          <PricingCard
            title="Leasing ab"
            priceFull="24"
            priceCents="90"
            details="Basierend auf 0,83 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Angebote, Order & Rechnungen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Mieten ab"
            priceFull="48"
            priceCents="90"
            details="Basierend auf 1,63 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit."
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Ideal für wachsende Unternehmen",
              "1 Benutzer",
            ]}
          />

          <PricingCard
            title="Ratenkauf ab"
            priceFull="74"
            priceCents="90"
            details="Basierend auf 2,49 € / Tag."
            details2="36 / 48 / 60 Monate Laufzeit. Restwert 10 %"
            features={[
              "Warenwirtschaft ModeProfi / CashTex",
              "Voller Support & Updates & Schulungen",
              "Inklusive Kassenbuch",
              "Eigentumserwerb nach Laufzeit",
              "1 Benutzer",
            ]}
          />
        </div>

        <div className="text-center mt-12 text-sm font-bold text-gray-900 tracking-widest uppercase">
          Alle Preise exkl. MwSt.
        </div>
      </Section>
      <KassensystemHardwareSection />
      <Section variant="gray1" className="tse-section">
        <ImageText
          imagePosition="left"
          imageSrc="/tse.png"
          imageAlt="Softwareentwicklung"
          title="TSE & Rechtssicherheit: Wir machen es einfach!"
        >
          <p>
            TSE-Pflicht oder neue Anforderungen vom Finanzamt? Kein Problem –
            wir kennen uns aus und übernehmen die Umsetzung der Technischen
            Sicherheitseinrichtung für Dich und informieren Dich über alle
            wichtigen Änderungen.
          </p>
          <ul className="ps-8">
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Kein zusätzlicher Aufwand:</strong> Regelmäßige und
              kostenlose Updates
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary " />{" "}
              <strong>Rechtssicher:</strong> TSE-konform – erfüllt KassenSichV
              und GoBD
            </li>
            <li>
              <Check className="inline -ms-8 text-brand-primary" />{" "}
              <strong>Zuverlässig:</strong> Datensicherheit auf deutschen
              Servern und nach höchsten Standards
            </li>
          </ul>
          <p>
            Mit der Kassensoftware von SDI arbeitest Du immer 100%
            finanzamtkonform und bist automatisch auf der sicheren Seite.
          </p>
        </ImageText>
      </Section>
    </ProductPage>
  );
}
